/**
 * JSON schema + limits for the compliance-audit internal-email-domains org
 * setting (CUR-823). Used by:
 * - the seed migration that creates the underlying ConfigurationItem row
 * - the server-side uncached accessor for value parsing + validation
 * - the configuration page that renders typed inputs against this schema
 *
 * Deliberately a copy of the auto-detect internal-domains schema, never a
 * re-export: the two settings are gated on different modules and have
 * separate lifecycles, so a change made to tune the message classifier must
 * never re-label compliance-audit evidence (and vice versa).
 */

/** Hard caps for admin-supplied input. Mirrors `AUTO_DETECT_INTERNAL_DOMAINS_LIMITS`. */
export const COMPLIANCE_AUDIT_INTERNAL_DOMAINS_LIMITS = {
    LIST_MAX: 25,
    ENTRY_MIN_LENGTH: 3,
    ENTRY_MAX_LENGTH: 254,
} as const;

// Same bare-domain pattern as the auto-detect schema (copied, not shared): rejects a
// full address like `info@curium.app`. Input is bounded by ENTRY_MAX_LENGTH, so the
// single quantified class cannot backtrack pathologically.
const INTERNAL_DOMAIN_PATTERN = '^[a-z0-9.-]+\\.[a-z]{2,}$';

/**
 * JSON schema for `COMPLIANCE_AUDIT_INTERNAL_EMAIL_DOMAINS`. Each entry is a bare
 * domain (no `@`, no path); used by `computeMessageDirection` at prompt-assembly
 * time to label each parent email's direction. Empty by default — when the per-org
 * overlay is absent, the accessor falls back to `Org.authenticatedDomain` only when
 * that value itself passes the bare-domain pattern; an empty resolved set means
 * direction stays UNKNOWN and the clause is suppressed.
 */
export const COMPLIANCE_AUDIT_INTERNAL_DOMAINS_SCHEMA = {
    type: 'array',
    items: {
        type: 'string',
        minLength: COMPLIANCE_AUDIT_INTERNAL_DOMAINS_LIMITS.ENTRY_MIN_LENGTH,
        maxLength: COMPLIANCE_AUDIT_INTERNAL_DOMAINS_LIMITS.ENTRY_MAX_LENGTH,
        pattern: INTERNAL_DOMAIN_PATTERN,
    },
    maxItems: COMPLIANCE_AUDIT_INTERNAL_DOMAINS_LIMITS.LIST_MAX,
    uniqueItems: true,
    default: [],
} as const;

export type ComplianceAuditInternalDomainsValue = string[];
